import { array } from 'fp-ts';
import { Fragment } from 'react';
import { AgeCategory, OccupancyData } from '@mews-commander/data-access';
import { getSortedAgeCategoryIdsByServiceId } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { WithServiceId } from '@mews-commander/types';
import { SpacedMultiplicationSign } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import { UnicodeEntity } from '@mews/enums';
import { keyBy } from '@mews/fp';
import { Stack } from '@optimus-web/core';
import { AgeCategoryName, AgeCategoryNameNew } from '../commander-ui';

interface ReservationPersonCountProps extends WithServiceId {
    reservationOccupancyData: Array<OccupancyData>;
}

export const ReservationPersonCount: React.FC<ReservationPersonCountProps> = ({
    serviceId,
    reservationOccupancyData,
}) => {
    const sortedAgeCategoryIds = useCommanderSelector((state) =>
        getSortedAgeCategoryIdsByServiceId(state, { serviceId })
    );

    if (!(array.isNonEmpty(reservationOccupancyData) && array.isNonEmpty(sortedAgeCategoryIds))) {
        return null;
    }

    const occupancyData = keyBy('ageCategoryId', reservationOccupancyData);

    return (
        <Stack wrap inline alignment="start">
            {sortedAgeCategoryIds.map((id) => {
                const { personCount, ageCategoryId } = occupancyData[id] ?? {};

                return !personCount ? null : (
                    <Fragment key={id}>
                        <span data-test-age-category-count={ageCategoryId}>
                            {personCount}
                            {SpacedMultiplicationSign}
                            <AgeCategoryName id={ageCategoryId} />
                        </span>
                        {UnicodeEntity.NoBreakSpace}
                    </Fragment>
                );
            })}
        </Stack>
    );
};

interface ReservationPersonCountPureProps {
    ageCategories: Array<AgeCategory>;
    occupancyData: Array<OccupancyData>;
}

export const ReservationPersonCountPure = ({ ageCategories, occupancyData }: ReservationPersonCountPureProps) => {
    if (!(array.isNonEmpty(occupancyData) && array.isNonEmpty(ageCategories))) {
        return null;
    }

    const occupancyDataRecord = keyBy('ageCategoryId', occupancyData);

    return (
        <>
            {ageCategories.map((ageCategory) => {
                const { id } = ageCategory;
                const { personCount } = occupancyDataRecord[id] ?? {};

                return personCount ? (
                    <span key={id} data-test-age-category-count={id}>
                        {personCount}
                        {SpacedMultiplicationSign}
                        <AgeCategoryNameNew ageCategory={ageCategory} />
                    </span>
                ) : null;
            })}
        </>
    );
};
